import { useContext, useMemo } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { Permission } from '../types';

/**
 * Hook per verificare i permessi dell'utente corrente
 * Utilizza il contesto di autenticazione per accedere ai permessi
 */
export const usePermissions = () => {
  const authContext = useContext(AuthContext);
  
  if (!authContext) {
    throw new Error('usePermissions deve essere usato all\'interno di AuthProvider');
  }

  const { user, permissions } = authContext;

  // Bug fix B11: allinea l'admin-shortcut a quello di AuthContext, che
  // riconosce sia `admin` sia la sua variante italianizzata `amministratore`.
  // Prima questo hook controllava SOLO `admin`, quindi un ruolo nominato
  // `amministratore` bypassava `AuthContext.hasPermission()` ma falliva
  // su `<PrivateRoute>` che usa questo hook → admin italianizzati restavano
  // fuori dalle route protette.
  const ADMIN_ROLE_NAMES = new Set(['admin', 'amministratore']);
  const isAdminRole = (): boolean => {
    if (!user || typeof user.roleId !== 'object' || !user.roleId) return false;
    return ADMIN_ROLE_NAMES.has((user.roleId as { name?: string }).name || '');
  };

  /**
   * Verifica se l'utente ha un permesso specifico
   * @param permissionName - Nome del permesso (es: "articles.create")
   * @returns true se l'utente ha il permesso
   */
  const hasPermission = useMemo(() => {
    return (permissionName: string): boolean => {
      if (!user) return false;

      // Verifica se ha il ruolo admin RBAC (ha tutti i permessi)
      if (isAdminRole()) {
        return true;
      }

      // Verifica nel sistema RBAC
      return permissions.some(p => p.name === permissionName);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [user, permissions]);

  /**
   * Verifica se l'utente ha almeno uno dei permessi specificati (OR logic)
   * @param permissionNames - Array di nomi permessi
   * @returns true se l'utente ha almeno uno dei permessi
   */
  const hasAnyPermission = useMemo(() => {
    return (permissionNames: string[]): boolean => {
      if (!user) return false;

      // Verifica se ha il ruolo admin RBAC (ha tutti i permessi)
      if (isAdminRole()) {
        return true;
      }

      return permissionNames.some(name =>
        permissions.some(p => p.name === name)
      );
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [user, permissions]);

  /**
   * Verifica se l'utente ha tutti i permessi specificati (AND logic)
   * @param permissionNames - Array di nomi permessi
   * @returns true se l'utente ha tutti i permessi
   */
  const hasAllPermissions = useMemo(() => {
    return (permissionNames: string[]): boolean => {
      if (!user) return false;

      // Verifica se ha il ruolo admin RBAC (ha tutti i permessi)
      if (isAdminRole()) {
        return true;
      }

      return permissionNames.every(name =>
        permissions.some(p => p.name === name)
      );
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [user, permissions]);

  /**
   * Verifica se l'utente può accedere a una risorsa
   * @param resource - Nome della risorsa (es: "articles")
   * @param action - Azione opzionale (es: "create")
   * @returns true se l'utente ha accesso
   */
  const canAccess = useMemo(() => {
    return (resource: string, action?: string): boolean => {
      if (!user) return false;

      // Verifica se ha il ruolo admin RBAC (ha tutti i permessi)
      if (isAdminRole()) {
        return true;
      }

      if (action) {
        // Verifica permesso specifico (es: "articles.create")
        return permissions.some(p => p.name === `${resource}.${action}`);
      } else {
        // Verifica se ha almeno un permesso per la risorsa
        return permissions.some(p => p.resource === resource);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [user, permissions]);

  /**
   * Ottiene tutti i permessi per una categoria
   * @param category - Nome della categoria
   * @returns Array di permessi
   */
  const getPermissionsByCategory = useMemo(() => {
    return (category: string): Permission[] => {
      return permissions.filter(p => p.category === category);
    };
  }, [permissions]);

  /**
   * Ottiene tutti i permessi per una risorsa
   * @param resource - Nome della risorsa
   * @returns Array di permessi
   */
  const getPermissionsByResource = useMemo(() => {
    return (resource: string): Permission[] => {
      return permissions.filter(p => p.resource === resource);
    };
  }, [permissions]);

  /**
   * Verifica se l'utente è admin RBAC
   */
  const isAdmin = useMemo(() => {
    if (!user) return false;
    return isAdminRole();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccess,
    getPermissionsByCategory,
    getPermissionsByResource,
    isAdmin
  };
};

